/**
 * YouTubeQuotaWidget — displays the daily quota consumption for a channel.
 *
 * Fetches on mount and refreshes every 30 s.
 * Colour coding: green <70 %, yellow 70-90 %, red >90 %.
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { getQuota } from '../../services/youtubePublishApi';
import type { YouTubeQuotaSnapshot } from '../../services/youtubePublishApi';

interface YouTubeQuotaWidgetProps {
  channel_id: string;
}

function barColor(pct: number): string {
  if (pct >= 0.9) return 'bg-red-500';
  if (pct >= 0.7) return 'bg-yellow-400';
  return 'bg-green-500';
}

function textColor(pct: number): string {
  if (pct >= 0.9) return 'text-red-400';
  if (pct >= 0.7) return 'text-yellow-400';
  return 'text-green-400';
}

export function YouTubeQuotaWidget({ channel_id }: YouTubeQuotaWidgetProps) {
  const { t } = useTranslation();
  const [quota, setQuota] = useState<YouTubeQuotaSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const load = async (): Promise<void> => {
      try {
        const data = await getQuota(channel_id);
        setQuota(data);
        setError(null);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load quota.');
      }
    };

    void load();
    intervalRef.current = setInterval(() => void load(), 30_000);
    return () => {
      if (intervalRef.current !== null) clearInterval(intervalRef.current);
    };
  }, [channel_id]);

  if (error) {
    return (
      <div className="bg-black/30 border border-white/10 rounded-lg px-4 py-3">
        <p className="text-red-400 text-xs">{error}</p>
      </div>
    );
  }

  if (!quota) {
    return (
      <div className="bg-black/30 border border-white/10 rounded-lg px-4 py-3">
        <p className="text-white/50 text-xs">{t('common.loading')}</p>
      </div>
    );
  }

  // Edge case: quota not configured at all.
  if (quota.daily_limit === 0) {
    return (
      <div className="bg-black/30 border border-white/10 px-3 py-2 rounded">
        <p className="text-white/70 text-xs">{t('youtube.quota.notConfigured')}</p>
      </div>
    );
  }

  // Zero-usage state: show explanatory message instead of a confusing empty bar.
  const hasNoUsage =
    quota.consumed === 0 && (quota.transactions === undefined || quota.transactions.length === 0);

  if (hasNoUsage) {
    return (
      <div className="bg-black/30 border border-white/10 px-3 py-2 rounded">
        <p className="text-white/70 text-xs">
          {t('youtube.quota.noUsageYet', { daily_limit: String(quota.daily_limit) })}
        </p>
      </div>
    );
  }

  const pct = quota.consumed / quota.daily_limit;
  const pctPx = Math.min(100, Math.round(pct * 100));

  return (
    <div className="bg-black/30 backdrop-blur-xl border border-white/10 rounded-lg px-4 py-3 space-y-2">
      {/* Bar */}
      <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${barColor(pct)}`}
          style={{ width: `${pctPx}%` }}
        />
      </div>
      {/* Label */}
      <p className={`text-xs ${textColor(pct)}`}>
        {t('youtube.quota.label', {
          consumed: String(quota.consumed),
          daily_limit: String(quota.daily_limit),
          reset_at: quota.reset_at,
        })}
      </p>
    </div>
  );
}
